import { Badge } from '@workspace/ui/components/badge';
import { CheckCircle2, Clock, XCircle, FileText, CreditCard, Ban } from 'lucide-react';
import { motion } from 'framer-motion';

interface StatusBadgeProps {
    value: string;
    label?: string;
    type?: 'statut' | 'etat' | 'default';
    className?: string;
    showIcon?: boolean;
}

/**
 * Badge coloré pour les statuts et états avec icônes et animations
 * Usage: <StatusBadge value="in_progress" label="En cours" type="statut" showIcon />
 */
export function StatusBadge({ value, label, type = 'default', className, showIcon = true }: StatusBadgeProps) {
    const getBadgeConfig = () => {
        if (type === 'statut') {
            switch (value) {
                case 'won':
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-green-500 text-white border-transparent font-medium',
                        icon: CheckCircle2
                    };
                case 'in_progress':
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-yellow-400 text-yellow-950 border-transparent font-medium',
                        icon: Clock,
                        animated: true
                    };
                case 'lost':
                case 'cancelled':
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-gray-200 text-gray-700 border-transparent font-medium',
                        icon: XCircle
                    };
                default:
                    return {
                        variant: 'outline' as const,
                        className: 'border-transparent font-medium',
                        icon: FileText
                    };
            }
        }

        if (type === 'etat') {
            const etatValue = typeof value === 'string' ? parseInt(value, 10) : parseInt(value);
            switch (etatValue) {
                case 1: // Devis
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-blue-100 text-blue-900 border-transparent font-medium',
                        icon: FileText
                    };
                case 2: // Contrat attente paiement
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-orange-100 text-orange-900 border-transparent font-medium',
                        icon: CreditCard,
                        animated: true
                    };
                case 3: // Contrat payé
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-green-500 text-white border-transparent font-medium',
                        icon: CheckCircle2
                    };
                case 4: // Contrat annulé
                    return {
                        variant: 'secondary' as const,
                        className: 'bg-red-100 text-red-900 border-transparent font-medium',
                        icon: Ban
                    };
                default:
                    return {
                        variant: 'outline' as const,
                        className: 'border-transparent font-medium',
                        icon: FileText
                    };
            }
        }

        return {
            variant: 'outline' as const,
            className: 'font-medium',
            icon: FileText
        };
    };

    const config = getBadgeConfig();
    const Icon = config.icon;

    return (
        <Badge {...config} className={`inline-flex items-center gap-1 ${config.className} ${className || ''}`}>
            {showIcon && Icon && (
                config.animated ? (
                    <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                        <Icon className="h-3 w-3" />
                    </motion.div>
                ) : (
                    <Icon className="h-3 w-3" />
                )
            )}
            {label || value}
        </Badge>
    );
}
